import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    IconButton,
    Pagination,
    Paper,
    Checkbox,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Tooltip
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    PlayArrow as ProcessIcon,
    CloudSync as SyncIcon,
    AutoFixHigh as ProcessAllIcon,
    Delete as DeleteIcon,
    Replay as ReplayIcon,
    CleaningServices as CleanupIcon,
    CheckCircleOutline as MarkDoneIcon
} from '@mui/icons-material';
import { receivedEmailsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { EMAIL_TYPES, EMAIL_TYPE_FILTERS, RECEIPT_STATUS, emailTypeLabel } from '../utils/emailTypes';
import { formatCurrency } from '../utils/helpers';
import UnlockPdfDialog from '../components/UnlockPdfDialog';

// What reading the email produced, in a few words
const outcomeText = (email) => {
    const x = email.extracted || {};
    switch (x.outcome) {
        case 'transactions':
            return x.count ? `${x.count} transaction${x.count === 1 ? '' : 's'}` : (x.balance ? 'Balance update' : 'No transactions');
        case 'bill':
            if (x.figures_in_attachment_only) return 'Figures are in the PDF';
            return [x.total_due !== null && x.total_due !== undefined && `Due ${formatCurrency(x.total_due)}`, x.due_date && `by ${x.due_date}`]
                .filter(Boolean).join(' ') || 'Bill saved';
        case 'payment':
            return x.amount ? `Paid ${formatCurrency(x.amount)}` : 'Payment';
        case 'receipt':
            return [x.merchant, x.amount && formatCurrency(x.amount)].filter(Boolean).join(' · ') || 'Receipt';
        case 'statement':
            return x.note || 'Statement';
        case 'failed':
            return 'Could not be read';
        case 'repeat':
            return 'Same as another email (read once)';
        default:
            return null;
    }
};

function ReceivedEmails() {
    const navigate = useNavigate();
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processingAll, setProcessingAll] = useState(false);
    const [processingSelected, setProcessingSelected] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pendingCount, setPendingCount] = useState(0);
    const [processedCount, setProcessedCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [markingProcessed, setMarkingProcessed] = useState(false);
    const [deletingSelected, setDeletingSelected] = useState(false);
    const [typeFilter, setTypeFilter] = useState('');
    const [deciding, setDeciding] = useState(false);
    // Statement email whose locked PDF is being opened
    const [unlockEmailId, setUnlockEmailId] = useState(null);
    const { showSuccess, showError } = useNotification();

    useEffect(() => {
        fetchEmails();
        setSelectedIds([]); // Clear selections on page or filter change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, typeFilter]);

    const fetchEmails = async () => {
        try {
            setLoading(true);
            const params = { page, limit: 20 };
            if (typeFilter) params.type = typeFilter;
            const response = await receivedEmailsApi.getAll(params);
            setEmails(response.data.emails);
            setTotalPages(response.data.totalPages);
            
            // Count pending and processed emails
            const pending = response.data.emails.filter(e => !e.processed).length;
            const processed = response.data.emails.filter(e => e.processed).length;
            setPendingCount(pending);
            setProcessedCount(processed);
        } catch (error) {
            console.error('Error fetching emails:', error);
            showError('Failed to load emails');
        } finally {
            setLoading(false);
        }
    };

    const handleViewEmail = async (emailId) => {
        try {
            const response = await receivedEmailsApi.getById(emailId);
            setSelectedEmail(response.data);
            setDialogOpen(true);
        } catch (error) {
            console.error('Error fetching email details:', error);
            showError('Failed to load email details');
        }
    };

    const handleProcessEmail = async (emailId) => {
        try {
            setProcessing(true);
            const response = await receivedEmailsApi.process(emailId);
            
            if (response.data.success) {
                showSuccess('Email sent for processing! Taking you to Processing Jobs...');
                
                // Navigate to processing jobs page to monitor progress (Email Jobs tab)
                setTimeout(() => {
                    navigate('/processing-jobs?tab=email');
                }, 1000);
            } else {
                showError(response.data.message || 'Processing failed');
            }
        } catch (error) {
            console.error('Error processing email:', error);
            showError(error.response?.data?.error || 'Failed to process email');
            setProcessing(false);
        }
    };

    const handleProcessPending = async () => {
        if (pendingCount === 0) {
            showError('No pending emails to process');
            return;
        }

        try {
            setProcessingAll(true);
            const response = await receivedEmailsApi.processPending();
            
            if (response.data.success) {
                const msg = response.data.message || `Found ${response.data.transactionsFound} transaction(s)`;
                showSuccess(`${msg}. Taking you to review...`);
                
                // Navigate to processing jobs page to review transactions (Email Jobs tab)
                setTimeout(() => {
                    navigate('/processing-jobs?tab=email');
                }, 1000);
            } else {
                showError(response.data.message || 'Failed to process emails');
                setProcessingAll(false);
            }
        } catch (error) {
            console.error('Error processing pending emails:', error);
            showError(error.response?.data?.error || 'Failed to process emails');
            setProcessingAll(false);
        }
    };

    const handleSyncEmails = async () => {
        try {
            setSyncing(true);
            const response = await receivedEmailsApi.sync();
            const { imported, skipped, total } = response.data;
            
            if (imported > 0) {
                showSuccess(`Found ${imported} new email(s)`);
                fetchEmails(); // Refresh list
            } else if (total === 0) {
                showSuccess('No new emails found');
            } else {
                showSuccess(`All ${total} email(s) already loaded`);
            }
        } catch (error) {
            console.error('Error syncing emails:', error);
            showError(error.response?.data?.error || 'Failed to check for new emails');
        } finally {
            setSyncing(false);
        }
    };

    // A receipt with no matching payment: save it as a cash payment, or dismiss it
    const handleReceiptDecision = async (emailId, action) => {
        try {
            setDeciding(true);
            await receivedEmailsApi.receiptDecision(emailId, action);
            showSuccess(action === 'add' ? 'Receipt added as a transaction' : 'Receipt dismissed');
            setDialogOpen(false);
            setSelectedEmail(null);
            fetchEmails();
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to save your decision');
        } finally {
            setDeciding(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedEmail(null);
    };

    const handleDeleteEmail = async (emailId) => {
        if (!window.confirm('Are you sure you want to delete this email? This will also remove it from S3 storage.')) {
            return;
        }

        try {
            setDeleting(emailId);
            await receivedEmailsApi.delete(emailId);
            showSuccess('Email deleted successfully');
            setSelectedIds(prev => prev.filter(id => id !== emailId));
            fetchEmails(); // Refresh list
        } catch (error) {
            console.error('Error deleting email:', error);
            showError(error.response?.data?.error || 'Failed to delete email');
        } finally {
            setDeleting(null);
        }
    };

    const handleSelectEmail = (emailId) => {
        setSelectedIds(prev => 
            prev.includes(emailId) 
                ? prev.filter(id => id !== emailId)
                : [...prev, emailId]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === emails.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(emails.map(e => e.id));
        }
    };

    const handleProcessSelected = async () => {
        if (selectedIds.length === 0) {
            showError('No emails selected');
            return;
        }

        try {
            setProcessingSelected(true);
            const response = await receivedEmailsApi.processSelected(selectedIds);
            
            if (response.data.success) {
                const msg = response.data.message || `Found ${response.data.transactionsFound} transaction(s)`;
                showSuccess(`${msg}. Taking you to review...`);
                setSelectedIds([]);
                
                // Navigate to processing jobs page to review transactions (Email Jobs tab)
                setTimeout(() => {
                    navigate('/processing-jobs?tab=email');
                }, 1000);
            } else {
                showError(response.data.message || 'Failed to process emails');
                setProcessingSelected(false);
            }
        } catch (error) {
            console.error('Error processing selected emails:', error);
            showError(error.response?.data?.error || 'Failed to process emails');
            setProcessingSelected(false);
        }
    };

    const handleClearProcessed = async () => {
        try {
            setClearing(true);
            const response = await receivedEmailsApi.clearProcessed();
            
            if (response.data.success) {
                showSuccess(response.data.message);
                setClearDialogOpen(false);
                fetchEmails(); // Refresh the list
            } else {
                showError('Failed to clear processed emails');
            }
        } catch (error) {
            console.error('Error clearing processed emails:', error);
            showError(error.response?.data?.error || 'Failed to clear processed emails');
        } finally {
            setClearing(false);
        }
    };

    const handleMarkAsProcessed = async () => {
        if (selectedIds.length === 0) {
            showError('No emails selected');
            return;
        }

        try {
            setMarkingProcessed(true);
            const response = await receivedEmailsApi.markProcessed(selectedIds);
            
            if (response.data.success) {
                showSuccess(response.data.message);
                setSelectedIds([]);
                fetchEmails(); // Refresh the list
            } else {
                showError('Failed to mark emails as processed');
            }
        } catch (error) {
            console.error('Error marking emails as processed:', error);
            showError(error.response?.data?.error || 'Failed to mark emails as processed');
        } finally {
            setMarkingProcessed(false);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) {
            showError('No emails selected');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} email(s)? This will also remove them from S3 storage.`)) {
            return;
        }

        try {
            setDeletingSelected(true);
            const response = await receivedEmailsApi.deleteSelected(selectedIds);
            
            if (response.data.success) {
                showSuccess(response.data.message);
                setSelectedIds([]);
                fetchEmails(); // Refresh the list
            } else {
                showError('Failed to delete emails');
            }
        } catch (error) {
            console.error('Error deleting selected emails:', error);
            showError(error.response?.data?.error || 'Failed to delete emails');
        } finally {
            setDeletingSelected(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5">Forwarded emails</Typography>
                    {pendingCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {pendingCount} pending email{pendingCount !== 1 ? 's' : ''} ready to process
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedIds.length > 0 && (
                        <>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<ReplayIcon />}
                                onClick={handleProcessSelected}
                                disabled={processingSelected || markingProcessed || deletingSelected || loading}
                            >
                                {processingSelected ? 'Processing...' : `Process ${selectedIds.length} Selected`}
                            </Button>
                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<MarkDoneIcon />}
                                onClick={handleMarkAsProcessed}
                                disabled={processingSelected || markingProcessed || deletingSelected || loading}
                            >
                                {markingProcessed ? 'Marking...' : 'Mark as Done'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteSelected}
                                disabled={processingSelected || markingProcessed || deletingSelected || loading}
                            >
                                {deletingSelected ? 'Deleting...' : 'Delete Selected'}
                            </Button>
                        </>
                    )}
                    {pendingCount > 0 && selectedIds.length === 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ProcessAllIcon />}
                            onClick={handleProcessPending}
                            disabled={processingAll || loading}
                        >
                            {processingAll ? 'Processing...' : `Process ${pendingCount} Pending`}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={handleSyncEmails}
                        disabled={syncing || loading}
                    >
                        {syncing ? 'Checking...' : 'Check for New Emails'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchEmails}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    {processedCount > 0 && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<CleanupIcon />}
                            onClick={() => setClearDialogOpen(true)}
                            disabled={loading || clearing}
                        >
                            Clear Processed
                        </Button>
                    )}
                </Box>
            </Box>

            <FormControl size="small" sx={{ minWidth: 240, mb: 2 }}>
                <InputLabel>Show</InputLabel>
                <Select
                    label="Show"
                    value={typeFilter}
                    onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                >
                    {EMAIL_TYPE_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </Select>
            </FormControl>

            {/* Cleanup encouragement message */}
            {processedCount > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Tip:</strong> You have {processedCount} processed email{processedCount !== 1 ? 's' : ''}. 
                    Clearing them will free up storage space and keep your inbox fast and organized.
                </Alert>
            )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : emails.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            {typeFilter ? 'No emails of this type' : 'No emails received yet'}
                        </Typography>
                        {!typeFilter && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Forward a bank email to your forwarding address to see it here.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < emails.length}
                                                checked={emails.length > 0 && selectedIds.length === emails.length}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>From</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Received</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {emails.map((email) => (
                                        <TableRow 
                                            key={email.id} 
                                            hover
                                            selected={selectedIds.includes(email.id)}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedIds.includes(email.id)}
                                                    onChange={() => handleSelectEmail(email.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{email.fromAddress}</TableCell>
                                            <TableCell>
                                                {email.subject || '(No subject)'}
                                                {outcomeText(email) && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {outcomeText(email)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {email.emailType ? (
                                                        <Tooltip title={email.typeConfidence ? `${Math.round(email.typeConfidence * 100)}% sure` : ''}>
                                                            <Chip
                                                                label={emailTypeLabel(email.emailType)}
                                                                color={EMAIL_TYPES[email.emailType]?.color || 'default'}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                                    )}
                                                    {email.emailType === 'merchant_receipt' && email.matchStatus && (
                                                        <Chip
                                                            label={RECEIPT_STATUS[email.matchStatus]?.label || email.matchStatus}
                                                            color={RECEIPT_STATUS[email.matchStatus]?.color || 'default'}
                                                            size="small"
                                                        />
                                                    )}
                                                    {email.pdfStatus === 'locked' && (
                                                        <Tooltip title="The statement PDF is password-protected. Click to open it with its password.">
                                                            <Chip
                                                                label="PDF locked"
                                                                color="warning"
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); setUnlockEmailId(email.id); }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {email.pdfStatus === 'unlocked' && (
                                                        <Chip label="PDF read" color="success" size="small" variant="outlined" />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatDate(email.receivedAt)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={email.processed ? 'Processed' : 'Pending'}
                                                    color={email.processed ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewEmail(email.id)}
                                                    title="View email"
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleProcessEmail(email.id)}
                                                    disabled={processing}
                                                    title={email.processed ? "Reprocess" : "Process now"}
                                                >
                                                    {email.processed ? <ReplayIcon /> : <ProcessIcon />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteEmail(email.id)}
                                                    disabled={deleting === email.id}
                                                    title="Delete email"
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(e, value) => setPage(value)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}

            {/* Email Detail Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedEmail?.subject || '(No subject)'}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedEmail && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                From:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {selectedEmail.fromAddress}
                            </Typography>

                            <Typography variant="subtitle2" color="text.secondary">
                                Received:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {formatDate(selectedEmail.receivedAt)}
                            </Typography>

                            <Typography variant="subtitle2" color="text.secondary">
                                Status:
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={selectedEmail.processed ? 'Processed' : 'Pending'}
                                    color={selectedEmail.processed ? 'success' : 'default'}
                                    size="small"
                                />
                                {selectedEmail.emailType && (
                                    <Chip label={emailTypeLabel(selectedEmail.emailType)} variant="outlined" size="small" />
                                )}
                                {outcomeText(selectedEmail) && (
                                    <Typography variant="body2" color="text.secondary">{outcomeText(selectedEmail)}</Typography>
                                )}
                            </Stack>
                            {selectedEmail.emailType === 'merchant_receipt' && selectedEmail.matchStatus === 'needs_decision' && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    No payment was found for this receipt. If you paid in cash or from an account
                                    PinePocket doesn't see, add it as a cash payment; otherwise dismiss it.
                                </Alert>
                            )}

                            {/* Show attachments if any */}
                            {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                                        Attachments ({selectedEmail.attachments.length}):
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        {selectedEmail.attachments.map((att, idx) => (
                                            <Chip
                                                key={idx}
                                                label={`${att.filename || 'unnamed'} (${Math.round((att.size || 0) / 1024)}KB)`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}

                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                                Email Content:
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mt: 1,
                                    backgroundColor: 'grey.50',
                                    maxHeight: 400,
                                    overflow: 'auto'
                                }}
                            >
                                {/* Prefer text body for clean display, fallback to HTML */}
                                {selectedEmail.textBody ? (
                                    <pre style={{ 
                                        whiteSpace: 'pre-wrap', 
                                        fontFamily: 'inherit', 
                                        fontSize: '0.875rem',
                                        margin: 0 
                                    }}>
                                        {selectedEmail.textBody}
                                    </pre>
                                ) : selectedEmail.htmlBody ? (
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody }}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                ) : (
                                    <Typography color="text.secondary">
                                        {selectedEmail.emailBody || 'Loading email content...'}
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedEmail?.emailType === 'merchant_receipt'
                        && ['unmatched', 'needs_decision'].includes(selectedEmail.matchStatus) && (
                        <>
                            <Button
                                onClick={() => handleReceiptDecision(selectedEmail.id, 'add')}
                                disabled={deciding}
                                variant="outlined"
                            >
                                Add as cash payment
                            </Button>
                            <Button
                                onClick={() => handleReceiptDecision(selectedEmail.id, 'dismiss')}
                                disabled={deciding}
                            >
                                Dismiss receipt
                            </Button>
                        </>
                    )}
                    {selectedEmail?.pdf?.locked && selectedEmail.pdfStatus !== 'unlocked' && (
                        <Button variant="outlined" onClick={() => setUnlockEmailId(selectedEmail.id)}>
                            Open PDF
                        </Button>
                    )}
                    {selectedEmail && (
                        <Button
                            onClick={async () => {
                                await handleProcessEmail(selectedEmail.id);
                                handleCloseDialog();
                            }}
                            disabled={processing}
                            startIcon={selectedEmail.processed ? <ReplayIcon /> : <ProcessIcon />}
                            variant="contained"
                        >
                            {selectedEmail.processed ? 'Reprocess' : 'Process Now'}
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            handleDeleteEmail(selectedEmail.id);
                            handleCloseDialog();
                        }}
                        disabled={deleting === selectedEmail?.id}
                        startIcon={<DeleteIcon />}
                        color="error"
                    >
                        Delete
                    </Button>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Clear Processed Emails Confirmation Dialog */}
            <Dialog
                open={clearDialogOpen}
                onClose={() => !clearing && setClearDialogOpen(false)}
            >
                <DialogTitle>Clear Processed Emails?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete all processed emails from both the database 
                        and cloud storage (S3).
                    </DialogContentText>
                    <Alert severity="success" sx={{ mt: 2 }}>
                        <strong>Benefits of clearing:</strong>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                            <li>Reduces your storage costs</li>
                            <li>Keeps your email list fast and responsive</li>
                            <li>Your transactions are already saved - emails are no longer needed</li>
                        </ul>
                    </Alert>
                    <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setClearDialogOpen(false)} 
                        disabled={clearing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleClearProcessed}
                        color="warning"
                        variant="contained"
                        disabled={clearing}
                        startIcon={clearing ? <CircularProgress size={16} /> : <CleanupIcon />}
                    >
                        {clearing ? 'Clearing...' : 'Clear All Processed'}
                    </Button>
                </DialogActions>
            </Dialog>

            <UnlockPdfDialog
                open={!!unlockEmailId}
                emailId={unlockEmailId}
                onClose={() => setUnlockEmailId(null)}
                onUnlocked={() => { setDialogOpen(false); fetchEmails(); }}
            />
        </Box>
    );
}

export default ReceivedEmails;

